import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Default to dark theme, check localStorage for saved preference
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('app-theme');
      return saved || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'light') {
      // Light theme CSS variables
      root.style.setProperty('--background', '0 0% 98%');
      root.style.setProperty('--foreground', '0 0% 10%');
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', '0 0% 10%');
      root.style.setProperty('--popover', '0 0% 100%');
      root.style.setProperty('--popover-foreground', '0 0% 10%');
      root.style.setProperty('--muted', '0 0% 92%');
      root.style.setProperty('--muted-foreground', '0 0% 45%');
      root.style.setProperty('--border', '0 0% 85%');
      root.style.setProperty('--input', '0 0% 85%');
      root.classList.remove('dark');
      root.classList.add('light');
      document.body.style.backgroundColor = '#fafafa';
      document.body.style.color = '#1a1a1a';
    } else {
      // Dark theme CSS variables (default)
      root.style.setProperty('--background', '0 0% 4%');
      root.style.setProperty('--foreground', '0 0% 93%');
      root.style.setProperty('--card', '0 0% 7%');
      root.style.setProperty('--card-foreground', '0 0% 93%');
      root.style.setProperty('--popover', '0 0% 7%');
      root.style.setProperty('--popover-foreground', '0 0% 93%');
      root.style.setProperty('--muted', '0 0% 10%');
      root.style.setProperty('--muted-foreground', '0 0% 63%');
      root.style.setProperty('--border', '0 0% 15%');
      root.style.setProperty('--input', '0 0% 15%');
      root.classList.remove('light');
      root.classList.add('dark');
      document.body.style.backgroundColor = '#0A0A0A';
      document.body.style.color = '#EDEDED';
    }
    
    // Save preference
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
